import React, { useCallback, useMemo, useState } from 'react';
import { Stack } from 'new/elements/Stack';
import { SeverityEnum } from '../types';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import { DarkMenuOptions } from '../components/DarkMenu/DarkMenu';

type Response = {
  buildLevelFilterOptions: () => DarkMenuOptions;
  appendSeverity: (severity: string) => void;
  selectedSeverityLabel: string;
  filterBy: {
    severity: string; // V1: Single severity selection (radio button) - string
    severities: string[]; // V2: Multiple severity selection (checkbox) - array
  };
};

const inputLabelStyle = {
  cursor: 'pointer',
  width: '100%',
  color: `var(--color-greyscale-100)`,
  fontSize: '12px',
  fontWeight: '500',
  lineHeight: '16px',
  fontFamily: 'Rubik',
};

export type UseFlowRunsLogsFilterResponse = Response;

export const useFilter = (): Response => {
  const isFlowInsightsV2Enabled = useFeatureFlag('UI_ENABLE_FLOW_INSIGHTS_V2');
  const [filterBy, onSetFilterBy] = useState({
    severity: '', // V1: single severity selection
    severities: [] as string[], // V2: multiple severity selection
  });

  const onSetSeverityFilter = useCallback((newValue: string) => {
    onSetFilterBy((prev) => ({
      ...prev,
      severity: prev.severity === newValue ? '' : newValue,
    }));
  }, []);

  const appendSeverity = useCallback((severity: string) => {
    const severityEnumValues = Object.values(SeverityEnum) as string[];
    const isValidSeverity = severityEnumValues.includes(severity);

    if (!isValidSeverity) {
      console.warn(`FlowRunsLogsFilter: Invalid severity value: ${severity}`);
      return;
    }

    onSetFilterBy((prev) => {
      const currentSeverities = prev.severities;
      if (currentSeverities.includes(severity)) {
        const updated = currentSeverities.filter((s) => s !== severity);
        return { ...prev, severities: updated };
      } else {
        return { ...prev, severities: [...currentSeverities, severity] };
      }
    });
  }, []);

  const isChecked = useCallback(
    (severity: string) => {
      if (isFlowInsightsV2Enabled) {
        return filterBy.severities.includes(severity);
      }
      return filterBy.severity === severity;
    },
    [isFlowInsightsV2Enabled, filterBy.severity, filterBy.severities],
  );

  /**
   * Return the selected severity label
   * V1: Return single severity
   * V2: Join multiple severities with " | "
   */
  const selectedSeverityLabel = useMemo(() => {
    if (isFlowInsightsV2Enabled)
      return filterBy.severities.length > 0 ? filterBy.severities.join(' | ') : '';
    return filterBy.severity;
  }, [isFlowInsightsV2Enabled, filterBy.severity, filterBy.severities]);

  const buildLevelFilterOptions = useCallback(() => {
    const inputType = isFlowInsightsV2Enabled ? 'checkbox' : 'radio';

    // For V2 checkboxes: only checkbox onChange should fire
    // For V1 radios: only MenuItem onClick should fire
    const getInputHandler = (severity: string) =>
      isFlowInsightsV2Enabled
        ? () => appendSeverity(severity)
        : (e: React.ChangeEvent) => e.preventDefault();

    const getMenuItemHandler = (severity: string) =>
      isFlowInsightsV2Enabled
        ? () => {} // No-op for checkboxes - let the input handle it
        : () => onSetSeverityFilter(severity);

    return [
      {
        label: 'error',
        optionContent: (
          <Stack direction="row" alignItems="center" gap="12px" width="100%">
            <input
              id={SeverityEnum.ERROR}
              type={inputType}
              className="checkbox"
              checked={isChecked(SeverityEnum.ERROR)}
              onChange={getInputHandler(SeverityEnum.ERROR)}
            />
            <label style={inputLabelStyle} htmlFor={SeverityEnum.ERROR}>
              error
            </label>
          </Stack>
        ),
        onClick: getMenuItemHandler(SeverityEnum.ERROR),
      },
      {
        label: 'info',
        optionContent: (
          <Stack direction="row" alignItems="center" gap="12px" width="100%">
            <input
              id={SeverityEnum.INFO}
              type={inputType}
              className="checkbox"
              checked={isChecked(SeverityEnum.INFO)}
              onChange={getInputHandler(SeverityEnum.INFO)}
            />
            <label style={inputLabelStyle} htmlFor={SeverityEnum.INFO}>
              info
            </label>
          </Stack>
        ),
        onClick: getMenuItemHandler(SeverityEnum.INFO),
      },
      {
        label: 'warning',
        optionContent: (
          <Stack direction="row" alignItems="center" gap="12px" width="100%">
            <input
              id={SeverityEnum.WARNING}
              type={inputType}
              className="checkbox"
              checked={isChecked(SeverityEnum.WARNING)}
              onChange={getInputHandler(SeverityEnum.WARNING)}
            />
            <label style={inputLabelStyle} htmlFor={SeverityEnum.WARNING}>
              warning
            </label>
          </Stack>
        ),
        onClick: getMenuItemHandler(SeverityEnum.WARNING),
      },
    ];
  }, [isFlowInsightsV2Enabled, isChecked, appendSeverity, onSetSeverityFilter]);

  return {
    filterBy,
    buildLevelFilterOptions,
    appendSeverity,
    selectedSeverityLabel,
  };
};
